import * as React from "react";
import {useState} from "react";

import "./customize-panel.scss";

import {cs}                             from "@Common/react/chain-services";
import {consumeContext, provideContext} from "@Common/react/context";
import {Load}                           from "@Common/react/load";
import {Load2}                          from "@Common/react/load2";
import {UseState}                       from "@Common/react/use-state";
import {AiBanner}                       from "@Common/ui-components/ai-banner/ai-banner";
import {FormattingModal}                from "@Common/ui-components/formatting-modal/formatting-modal";
import {ImageGuidelinesModal}           from "@Common/ui-components/image-guidelines-modal/image-guidelines-modal";
import {PhotoDashboard,
        PhotoDashboardTabs}             from "@Common/ui-components/photo-dashboard/photo-dashboard";

import {ChangeListingModal} from "../../cc-view/change-listing-modal/change-listing-modal";
import {collectUserData}    from "../../cc-view/exhibit-xpresso/collect-user-data";

import {ColorScheme}             from "./customize-panel-components/colorScheme";
import {Content}                 from "./customize-panel-components/content";
import {Headshots}               from "./customize-panel-components/headshots";
import {JustListedJustSoldField} from "./customize-panel-components/justListedJustSoldField";
import {Logos}                   from "./customize-panel-components/logos";
import {OfficeReturnAddress}     from "./customize-panel-components/officeReturnAddress";
import {OrderDetails}            from "./customize-panel-components/orderDetails";
import {Photos}                  from "./customize-panel-components/photos";
import {QrCodes}                 from "./customize-panel-components/qrCodes";
import {Specifications}          from "./customize-panel-components/specifications";
import {UserContactInfo}         from "./customize-panel-components/userContactInfo";
import {isPickPack}              from "@Common/logic/item/is-pick-pack";

export const CustomizePanel = ({orderId, item, scrollControl, companyPrefs, companyProfile, orderData, imageFields, apis, setUnsavedChanges, isListingsEligible, listing, match, error, onRendered = null}) => {

    async function updateImage(orderId, data) {
        let imageFieldsUpdated = await apis.next.OrderManager.updateOrderImageFields(orderId, {...data}, match.params.component_id);
        imageFields.reload();
        setUnsavedChanges();
        return imageFieldsUpdated;
    }

    // replaced hack with another hack. This one does not result in a "Cannot update during an existing state transition" warning from React
    // also per our last PI planning, we will be getting rid of the Step navigations at top of page so this won't be around long anyway
        // Previous hack comment from PROJ-32152:
            // call onRendered if supplied - this is a hack to allow the header to be re-rendered after the scrollControl refs are created
            // {apisLoaded && onRendered && onRendered()}
    {React.useEffect(() => {
        if (onRendered) {
        onRendered();
        }
    }, [onRendered])}

    return cs(
    consumeContext("apis"),
    ["itemSku", (_, next) => next(!match.params.component_id ? item.sku : item.components[match.params.component_id].sku)],
    ["changeListingModal", ({}, next) => ChangeListingModal({
        title: listing && Object.keys(listing).length !== 0 ? listing?.location?.streetAddress : "No listing Selected",
        listingId: listing._id,
        getItem: async () => {
            return apis.next.item.getItem(match.params.item_id);
        },
        host: null,
        stateId: null,
        clearChanged: () => null,
        orderId,
        isBuild3: true,
        next,
    })],
    ["headshots", ({}, next) => next(
        orderData.headshotFields
    )],
    ["photos", ({}, next) => next(
        orderData.photoFields
    )],
    ["designations", ({}, next) => next(
        orderData.designations
    )],
    ["logos", ({}, next) => next(
        orderData.logoFields
    )],
    ["qrCodes", ({}, next) => next(
        orderData.qrCodeFields
    )],
    ["photoDashboard", ({}, next) => PhotoDashboard({next})],
    ["statusMessage", ({}, next) => UseState({next})],
    ["onApplyImageFromPhotoDashboard", ({statusMessage}, next) => next(async (imageArea, assetType, imageId, imagePath, callback) => {
        await updateImage(orderId, {[imageArea.name] : imageId});
        statusMessage.onChange(`Selected ${assetType} image asset ${imageId} (${imagePath}) for image area ${imageArea.name}.`);
        setUnsavedChanges();
        callback();
    })],
    ["onApplyMultipleImagesFromPhotoDashboard", ({photos}, next) => next(async (imageArea, selections, callback) => {
        let imageMapping = {};
        photos.forEach((photo, index) => {
            imageMapping[photo.idPrimary] = selections[index]?.imageId || 0; // fallback to 0 is required to clear an image area
        });
        await updateImage(orderId, imageMapping);
        setUnsavedChanges();
        callback();
    })],
    ["colorScheme", ({apis, itemSku}, next) => Load2({
        fetch: async () => !orderId ? null : await apis.next.order.getColorSet(itemSku.back, itemSku.front),
        next,
    })],
    ["content", ({}, next) => next(orderData.contentFields)],
    ["specifications", ({}, next) => next(orderData.specificationFields)],
    ["justListedJustSoldField", ({specifications}, next) => next(
        item?.programData?.id != 'MAIN' && specifications.value?.filter(specField => specField.fieldKey === "field_65")
    )],
    ["userLevel", ({apis}, next) => Load2({
        fetch: async () => await apis.next.session.getUserLevels(),
        next,
    })],
    ["templateOptions", ({apis, itemSku}, next) => Load2({
        fetch: async () => !orderId ? {} : await apis.next.getTemplateOptions(itemSku),
        next,
    })],
    ["allowedPhotoTabs", ({apis, itemSku}, next) => Load2({
        fetch: async () => {
            if (!orderId) return {};

            const allowedGalleryTabs = await apis.next.gallery.getAllowedPhotoGalleryTabs({idCompany: item.idCompany, back: itemSku.back });

            const mapLegendNamesToPhotoDashboardTabNames = {
                'allow_property_photo_upload': PhotoDashboardTabs.orderGallery,
                'allow_property_photo_gallery': PhotoDashboardTabs.propertyPhotos,
                'listing_gallery': PhotoDashboardTabs.listings,
                'allow_property_photo_services': PhotoDashboardTabs.photoIntegrations,
                'allow_property_photo_previous_order': PhotoDashboardTabs.previousOrders,
                'Headshot Gallery': PhotoDashboardTabs.headshots,
                'Headshot Upload Gallery': PhotoDashboardTabs.headshots,
                'Designation Gallery': PhotoDashboardTabs.designations,
                'Logo Gallery': PhotoDashboardTabs.logos,
            };

            listing._id && allowedGalleryTabs.unshift('listing_gallery');

            return allowedGalleryTabs.map(name => mapLegendNamesToPhotoDashboardTabNames[name]);
        },
        next,
    })],
    ["userData", ({itemSku}, next) =>
        collectUserData({
            ccStateId: '',
            templateIds: itemSku.back && itemSku.front != itemSku.back ? [itemSku.front, itemSku.back] : [itemSku.front],
            listing,
            mockData: null,
            userCreatedStateId: null,
            next
        })
    ],
    ({userData}, next) => provideContext({"userData": userData}, next),
    ["colorFields", ({apis}, next) => Load({
        fetch: async () => await apis.next.OrderManager.getColorFields(orderId, match.params.component_id),
        next
    })],
    ["suggestedComments", ({apis, itemSku}, next) => Load({
        fetch: async () => {
            return await apis.next.session.getSuggestedComments({request: {
                    idTemplate: itemSku.front,
                    orderType: item.orderType,
                   // styleId: we already getting styleid in API service based on templte ID.
                    //productId: templateOptions?.product?.productId,
                    agentId: item.idAgent,
                    branchId: item.idBranch,
                    companyId: item.idCompany,
                    programId: item.idProgram,
                }})
        },
        next
    })],
    ({suggestedComments}, next) => provideContext({"suggestedComments": suggestedComments}, next),
    ["formattingModal", ({}, next) => FormattingModal({
        next
    })],
    ["imageGuidelinesModal", ({}, next) => ImageGuidelinesModal({
        next
    })],
    ["headshotEditable", ({userLevel}, next) => next(
        (userLevel.value?.userLevel == 5 && companyPrefs.agentCanEditHeadshot) ? true :
        (userLevel.value?.userLevel == 4 && companyPrefs.officeCanEditHeadshot1) ? true :
        (userLevel.value?.userLevel <= 3) ? true : false
    )],
    ["logoEditable", ({userLevel}, next) => next(
        (userLevel.value?.userLevel == 5 && companyPrefs.agentCanEditLogos) ? true :
        (userLevel.value?.userLevel == 4 && companyPrefs.officeCanEditLogos) ? true :
        (userLevel.value?.userLevel <= 3) ? true : false
    )],
    ["photosEditable", ({}, next) => next(
        companyPrefs.allowPhotoEditor ? true : false
    )],
    ["orderName", ({apis}, next) => Load2({
        fetch: async () => !orderId ? null : await apis.next.OrderManager.getOrderColumn(orderId, "jobName"),
        next,
    })],
    ["isDualAgent", ({templateOptions}, next) => next( templateOptions.loading == false ? templateOptions.value.options.find(option => {return option.devKey == "enable_dual_agent_format" })?.value == 1 ? 1 : 0 : undefined )],
    ["agentEditBranch", ({}, next) => next(companyPrefs.agentEditBranch)],
    ["agentEditDisplay", ({}, next) => next(companyPrefs.agentEditDisplay)],
    ["branchEditDisplay", ({}, next) => next(companyProfile.branchEditDisplay)],
    ["branchEditAgentDisplay", ({}, next) => next(companyProfile.branchEditAgentDisplay)],
    ["showFormattingImageGuidelines", ({}, next) => next(companyPrefs.allowFormattingAndGuidelines && item && !isPickPack(item))],
    ["apisLoaded", ({content, specifications, userLevel, templateOptions, allowedPhotoTabs, orderName}, next) => next(orderName.loading == false && content.loading == false && userLevel.loading == false && templateOptions.loading == false && specifications.loading == false && allowedPhotoTabs.loading == false)],
    ["branch", ({apis}, next) => Load({
        fetch: async () => await apis.next.session.getBranch({officeId: item.idBranch}),
        next
    })],
    ["assignedDomain", ({templateOptions, branch}, next) => next(
        templateOptions.loading == false ? (
            templateOptions.value.assignedDomain ? {"assignDomain":templateOptions.value.assignedDomain, "domainWrap":templateOptions.value?.domainWrap} :
            branch?.assignedDomain ? {"assignDomain":branch?.assignedDomain, "domainWrap":branch?.assignedDomain} :
            {"assignDomain":companyPrefs?.assignedDomain, "domainWrap":companyPrefs.domainWrap}
            ) : ''
    )],

    ["agentDisplayData", ({}, next) => next(orderData.userDisplayFields)],
    ["branchDisplayData", ({}, next) => next(orderData.branchDisplayFields)],

    ({
        colorScheme, colorFields, content, specifications, agentEditBranch, agentEditDisplay, userLevel, isDualAgent,
        apisLoaded, branchEditDisplay, branchEditAgentDisplay, justListedJustSoldField, headshots, photos, designations, logos, qrCodes, headshotEditable, logoEditable,
        photosEditable, allowedPhotoTabs, assignedDomain, photoDashboard, onApplyImageFromPhotoDashboard,
        onApplyMultipleImagesFromPhotoDashboard, formattingModal, imageGuidelinesModal,
        changeListingModal, templateOptions, branch, orderName, agentDisplayData, branchDisplayData,showFormattingImageGuidelines
    }) => (
        <div className="customize-panel-pl5">
            <div className="orderAndListingCont">
                {(
                    specifications.value?.some(field => field.idDisplayStyle == 4 || field.idDisplayStyle == 11) ||
                    content.value?.some(field => field.idDisplayStyle == 4 || field.idDisplayStyle == 11)
                ) && <AiBanner/>}
                {apisLoaded &&
                    <OrderDetails
                        orderId={orderId}
                        orderName={orderName}
                        domRef={scrollControl.ref("b3-order-details")}
                        isListingsEligible={isListingsEligible && !match.params.component_id}
                        changeListingModal={changeListingModal.show}
                        listing={listing?.location?.streetAddress ? listing?.location?.streetAddress : "No listing Selected"}
                        companyPrefs={companyPrefs}
                        item={item}
                        userLevel={userLevel.value?.userLevel}
                    />
                }
            </div>
            {apisLoaded &&
                <ColorScheme
                    colorSchemeData={{"colorSet":colorScheme, "selected":colorFields?.colorField2}}
                />
            }
            {apisLoaded &&
            justListedJustSoldField.length > 0 &&
            justListedJustSoldField[0]?.idDisplayStyle !== 10 &&
            justListedJustSoldField[0]?.idDisplayStyle !== 11 &&
                <JustListedJustSoldField
                    justListedJustSoldFieldData={justListedJustSoldField[0]}
                    domRef={undefined}
                    setUnsavedChanges={setUnsavedChanges}
                />
            }
            {(apisLoaded && item.idAgent != "Branch Order" && agentDisplayData.length && templateOptions.value?.build_path != "cbfl_gen_buscard_v2.inc" && templateOptions.value?.build_path != "gen_buscard_v2.inc" && templateOptions.value?.build_path != "sothebys_gen_buscard.inc" && templateOptions.value?.agentDisplayEnabled) ?
                <UserContactInfo
                    userData={orderData.userDisplayFields}
                    userLevel={userLevel.value?.userLevel}
                    agentEditDisplay={agentEditDisplay}
                    branchEditAgentDisplay={branchEditAgentDisplay}
                    isDualAgent={isDualAgent}
                    setUnsavedChanges={setUnsavedChanges}
                    domRef={scrollControl.ref("b3-contact-info")}
                    formattingModal={formattingModal}
                />
            : null}
            {(apisLoaded && branchDisplayData.length && templateOptions.value?.build_path != "cbfl_gen_buscard_v2.inc" && templateOptions.value?.build_path != "gen_buscard_v2.inc" && templateOptions.value?.build_path != "sothebys_gen_buscard.inc" && templateOptions.value?.branchDisplayEnabled) ?
                <OfficeReturnAddress
                    addressData={orderData.branchDisplayFields}
                    userLevel={userLevel.value?.userLevel}
                    agentEditBranch={agentEditBranch}
                    branchEditDisplay={branchEditDisplay}
                    setUnsavedChanges={setUnsavedChanges}
                    formattingModal={formattingModal}
                />
            : null}
            {apisLoaded && headshots.length > 0 &&
                <Headshots
                    orderId={orderId}
                    headshotData={headshots}
                    domRef={scrollControl.ref("b3-images-info")}
                    updateImage={updateImage}
                    showGallery={(data) => photoDashboard.show({
                        orderId: orderId,
                        ...data
                    })}
                    onApplyImageFromPhotoDashboard={onApplyImageFromPhotoDashboard}
                    imageGuidelinesModal={imageGuidelinesModal}
                    editable={headshotEditable}
                    allowedGalleryTabs={[PhotoDashboardTabs.headshots]}
                    setUnsavedChanges={setUnsavedChanges}
                />
            }
            {apisLoaded && photos.length > 0 &&
                <Photos
                    orderId={orderId}
                    photoData={photos}
                    domRef={scrollControl.ref("b3-images-info")}
                    updateImage={updateImage}
                    showGallery={(data) => photoDashboard.show({
                        orderId: orderId,
                        listing: listing._id ? listing : null,
                        ...data
                    })}
                    onApplyImageFromPhotoDashboard={onApplyImageFromPhotoDashboard}
                    onApplyMultipleImagesFromPhotoDashboard={onApplyMultipleImagesFromPhotoDashboard}
                    imageGuidelinesModal={imageGuidelinesModal}
                    editable={photosEditable}
                    allowedGalleryTabs={allowedPhotoTabs.value}
                    listing={listing}
                    setUnsavedChanges={setUnsavedChanges}
                />
            }
            {apisLoaded && logos.length > 0 &&
                <Logos
                    orderId={orderId}
                    logoData={logos}
                    domRef={scrollControl.ref("b3-images-info")}
                    updateImage={updateImage}
                    showGallery={(data) => photoDashboard.show({
                        orderId: orderId,
                        useEpsDesignations: true,
                        ...data
                    })}
                    onApplyImageFromPhotoDashboard={onApplyImageFromPhotoDashboard}
                    imageGuidelinesModal={imageGuidelinesModal}
                    editable={designations.value ? false : logoEditable}
                    allowedGalleryTabs={designations.value ? [PhotoDashboardTabs.designations] : [PhotoDashboardTabs.logos]}
                    setUnsavedChanges={setUnsavedChanges}
                    logoType={designations.value ? "designation" : "logo"}
                    showFormattingImageGuidelines={showFormattingImageGuidelines}
                />
            }
            {apisLoaded && qrCodes.length > 0 &&
                <QrCodes
                    orderId={orderId}
                    qrCodeData={qrCodes}
                    domRef={scrollControl.ref("b3-images-info")}
                    updateImage={updateImage}
                    showGallery={(data) => photoDashboard.show({
                        orderId: orderId,
                        ...data
                    })}
                    onApplyImageFromPhotoDashboard={onApplyImageFromPhotoDashboard}
                    imageGuidelinesModal={imageGuidelinesModal}
                    editable={false}
                    allowedGalleryTabs={[PhotoDashboardTabs.qrCodes]}
                    setUnsavedChanges={setUnsavedChanges}
                />
            }
            {apisLoaded && content.value?.length > 0 &&
                <Content
                    idOrder={orderId}
                    contentData={content.value?.filter( (datapoint) => datapoint.contentGroup ).sort((a, b) => a.ordinal - b.ordinal)}
                    domRef={scrollControl.ref("b3-content-info")}
                    setUnsavedChanges={setUnsavedChanges}
                    formattingModal={formattingModal}
                    companyPrefs={companyPrefs}
                />
            }
            {apisLoaded && specifications.value.length > 0 &&
                <Specifications
                    idOrder={orderId}
                    domainData={assignedDomain}
                    specsData={specifications.value?.filter( (datapoint) => {
                        // If datapoint.fieldKey is "field_65"
                        if (datapoint.fieldKey == "field_65") {
                            // Include the record if item.programData.id is not main
                            return item.programData.id == 'MAIN';
                        }
                        // Include all other records
                        return true
                        }).sort((a, b) => a.ordinal - b.ordinal)}
                    domRef={ apisLoaded && content.value.length > 0 ? scrollControl.ref("b3-specifications-info") : scrollControl.ref("b3-content-info")}
                    setUnsavedChanges={setUnsavedChanges}
                    formattingModal={formattingModal}
                    companyPrefs={companyPrefs}
                    maxLines={templateOptions.value.storeMaxNumberOfLinesAllowed}
                    maxNumberOfLinesEnabled={templateOptions.value.maxNumberOfLinesAllowed}
                    branchDetails={branch}
                    showFormattingImageGuidelines={showFormattingImageGuidelines}
                    error={error}
                />
            }
        </div>
    )
)};
